/**
 * THE CAPTION UNDER THE TOP BAR — what period, ON WHOSE CLOCK, against what, and
 * covering what.
 *
 * ONE CLOCK (R-T12, Plan C WP-C3). An owner's or a manager's dashboard is on
 * THEIR clock — one window and one set of day buckets for every building, so
 * the tiles and the breakdowns add up by construction. The caption says which
 * clock that is (`<ClockNote>`: "Your time — Auckland"), and, when the
 * buildings are in several zones, says once that the figures are on one clock
 * all the same. It used to say the buildings "did not start their day at the
 * same instant", which described per-building windows that no longer exist.
 *
 * Every sentence here is ported from analytics-scope-bar.tsx, and each one is a
 * way the screen could otherwise state a falsehood:
 *
 *  · A PERIOD whose bounds the reader cannot see is a period they will assume,
 *    and the ZONE is half of what a bound means.
 *  · A CROSS-ZONE answer did not start its day at the same instant in every
 *    building, and nothing else on the screen says so.
 *  · A COMPARISON of two windows of different lengths carries no percentage,
 *    anywhere, and the caption is where that is announced once.
 *  · WHAT THE FIGURES COVER and WHAT THE READER CAN REACH are two different
 *    facts. Collapsing them is how a filtered screen ends up captioned with the
 *    caller's whole access — with one building selected the old caption still
 *    read "these figures cover every building in your organisation (3 visible)",
 *    which was true of the ACCESS and false of the numbers above it.
 */
import React from 'react';
import {
  AnalyticsScope,
  AnalyticsSpan,
  AnalyticsWindow,
} from '../../../../../api/analytics-client';
import { Caption } from '../dashboard-screen.styled';
import { formatInstant, formatSpan } from '../charts/chart-theme';
import { Clock, zoneLabel } from '../../../../../function-utils/building-time';
import { ClockNote } from '../../../../clock-note/clock-note';

const WINDOW_SOURCE_NOTE: Record<string, string> = {
  shift: 'resolved from your own shift record',
  last_12h:
    'you have no shift on record for this period, so this is the last 12 hours instead',
  explicit: 'the exact instants requested',
};

export interface ScopeCaptionProps {
  window?: AnalyticsWindow;
  compareWindow?: AnalyticsSpan | null;
  scope?: AnalyticsScope;
  curSpanMs?: number;
  prevSpanMs?: number;
  likeForLike?: boolean;
  /** The building or portfolio the figures are NARROWED to, if any. Separate
   *  from `scope`, and both are printed — see the header. */
  focusLabel?: string;
  /** The clock the answer is on, from the response's `tz` / `tz_source`. */
  clock?: Clock;
}

/* The buildings' own zones, named for a person and without repeats: two
   buildings in Toronto and New York are one clock to the reader. */
const zoneNames = (zones: string[]): string[] =>
  Array.from(new Set(zones.map(zone => zoneLabel(zone) || zone)));

export const ScopeCaption: React.FC<ScopeCaptionProps> = ({
  window: answerWindow,
  compareWindow,
  scope,
  curSpanMs,
  prevSpanMs,
  likeForLike,
  focusLabel,
  clock,
}) => {
  const zones = zoneNames(scope?.zones || []);
  const sourceNote = answerWindow
    ? WINDOW_SOURCE_NOTE[answerWindow.source]
    : undefined;

  return (
    <Caption data-testid="scope-caption">
      {answerWindow ? (
        <>
          Period <strong>{formatInstant(answerWindow.from, answerWindow.tz)}</strong> to{' '}
          <strong>{formatInstant(answerWindow.to, answerWindow.tz)}</strong>
          {sourceNote ? ` (${sourceNote})` : ''}.{' '}
          {clock ? (
            <>
              <strong>
                <ClockNote clock={clock} inline />
              </strong>
              .{' '}
            </>
          ) : (
            <>
              Measured in <strong>{zoneLabel(answerWindow.tz) || answerWindow.tz}</strong>.{' '}
            </>
          )}
        </>
      ) : (
        'Resolving the period… '
      )}

      {zones.length > 1 ? (
        <>
          These buildings are in <strong>{zones.length}</strong> time zones (
          {zones.join(', ')}); every figure here is on one clock, the same day for every
          building.{' '}
        </>
      ) : zones.length === 1 ? (
        <>
          Every building in scope is on <strong>{zones[0]}</strong>
          {zones[0] === 'UTC' ? '' : ' time'}.{' '}
        </>
      ) : null}

      {compareWindow && compareWindow.from && compareWindow.to ? (
        <>
          Compared with{' '}
          <strong>{formatInstant(compareWindow.from, answerWindow?.tz || 'UTC')}</strong>{' '}
          to{' '}
          <strong>{formatInstant(compareWindow.to, answerWindow?.tz || 'UTC')}</strong>
          {likeForLike === false && curSpanMs != null && prevSpanMs != null
            ? ` — ${formatSpan(curSpanMs)} against ${formatSpan(
                prevSpanMs
              )}, which are different lengths, so no percentage change is shown anywhere on this page.`
            : '.'}{' '}
        </>
      ) : null}

      {focusLabel ? (
        <>
          Narrowed to <strong>{focusLabel}</strong> — the figures above cover that alone
          {scope ? (
            <>
              , out of the <strong>{scope.buildings}</strong> building
              {scope.buildings === 1 ? '' : 's'} you can see
            </>
          ) : null}
          .
        </>
      ) : scope ? (
        <>
          {scope.all
            ? 'These figures cover every building in your organisation'
            : 'These figures cover the buildings assigned to you'}{' '}
          (<strong>{scope.buildings}</strong> visible).
        </>
      ) : null}
    </Caption>
  );
};
